import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { useStores } from 'index';
import { IResult } from 'services/Result';
import { AxiosResponseHelper } from 'utilities/AxiosResponseHelper';
import { t } from 'utilities/TranslationUtils';
import { sessionPlayerUrl } from 'routePaths';
import { SessionNeighbourDto, SessionNeighboursDto } from 'types/models/MeasurementDetail';
import { countTitle } from './kickWording';

// Neighbours per (session, player), kept for the SPA session so flipping back and forth is instant.
const neighboursCache = new Map<string, SessionNeighboursDto>();

interface SessionNeighbourLinksProps {
  sessionId: string;
  playerId: string | null;
}

interface NeighbourLinkProps {
  direction: 'previous' | 'next';
  neighbour: SessionNeighbourDto | null;
  onOpen: (neighbour: SessionNeighbourDto) => void;
}

const NeighbourLink = ({ direction, neighbour, onOpen }: NeighbourLinkProps) => (
  <button
    type='button'
    className={`session-neighbour session-neighbour--${direction}`}
    disabled={!neighbour}
    onClick={() => neighbour && onOpen(neighbour)}
    data-test={`session-neighbour-${direction}`}
  >
    <span className='session-neighbour__label'>
      {direction === 'previous' ? '‹ ' : ''}{t(direction === 'previous' ? 'measurementDetail.previousSession' : 'measurementDetail.nextSession')}{direction === 'next' ? ' ›' : ''}
    </span>
    <span className='session-neighbour__line'>
      {neighbour
        ? `${dayjs(neighbour.capturedAtUtc).format('DD MMM YYYY')} · ${countTitle(neighbour.kind === 'ThrowIn', neighbour.kickCount)}`
        : '—'}
    </span>
  </button>
);

/**
 * The player's previous and next session of the same kind (date + number of kicks / throws), from the player snapshot.
 * Always renders both slots — an absent neighbour is a disabled placeholder — so the toolbar never shifts as data loads.
 */
const SessionNeighbourLinks: React.FunctionComponent<SessionNeighbourLinksProps> = ({ sessionId, playerId }) => {
  const { apiServiceAxiosInstance: api } = useStores();
  const history = useHistory();
  const key = `${sessionId}:${playerId ?? ''}`;
  const [neighbours, setNeighbours] = useState<SessionNeighboursDto | null>(() => neighboursCache.get(key) ?? null);

  useEffect(() => {
    const cached = neighboursCache.get(key);
    if (cached) {
      setNeighbours(cached);
      return undefined;
    }
    setNeighbours(null);
    if (!playerId) return undefined;

    let cancelled = false;
    api
      .get<IResult<SessionNeighboursDto>>(`api/SessionNeighbours?sessionId=${encodeURIComponent(sessionId)}&playerId=${encodeURIComponent(playerId)}`)
      .then((response) => {
        if (cancelled || !AxiosResponseHelper.IsSuccessfulResponse(response) || !response.data?.data) return;
        neighboursCache.set(key, response.data.data);
        setNeighbours(response.data.data);
      })
      .catch(() => undefined); // best-effort: the links just stay disabled
    return () => { cancelled = true; };
  }, [api, key, sessionId, playerId]);

  const open = (neighbour: SessionNeighbourDto) => {
    if (playerId) history.push(sessionPlayerUrl(neighbour.sessionId, playerId));
  };

  return (
    <div className='session-neighbours' data-test='session-neighbours'>
      <NeighbourLink direction='previous' neighbour={neighbours?.previous ?? null} onOpen={open} />
      <NeighbourLink direction='next' neighbour={neighbours?.next ?? null} onOpen={open} />
    </div>
  );
};

export default SessionNeighbourLinks;
